import { CompanyOpenTicketCount, PRODUCT_FEATURES } from "@signalist/shared-types";

import { format, formatDistanceToNow } from "date-fns";

import {
	ActivityStats,
	BillingInterval,
	SubscriptionType,
	SupportCompany,
} from "../schema/support-dashboard";

import type { TFunction } from "i18next";

export const sumField = (
	company: SupportCompany,
	field: "emailsSentToday" | "linkedinInvitesToday" | "linkedinMessagesToday"
): number => company.runningCampaigns.reduce((sum, c) => sum + (Number(c[field]) || 0), 0);

/**
 * "Last active" (PREC-4335): how long ago `lastActiveAt` was, or a dash for an account (or
 * company) not active since the column exists. Distinct from "Last Activity", which is a
 * product-usage day (PREC-4336 part C).
 */
export const formatLastActive = (at: string | null | undefined): string => {
	if (!at) {
		return "—";
	}

	const date = new Date(at);

	return Number.isNaN(date.getTime()) ? "—" : formatDistanceToNow(date, { addSuffix: true });
};

const FEATURE_LABELS: ReadonlyMap<string, string> = new Map(
	PRODUCT_FEATURES.map((feature) => [feature.key, feature.label])
);

/** A `YYYY-MM-DD` day as the UI reads it, in UTC ("28 Oct 2026"); a dash for none or junk. */
export const formatActivityDate = (day: string | null | undefined): string =>
	formatDay(day, "d MMM yyyy");

/** A `YYYY-MM-DD` day short, for "12 on 28 Oct"; a dash for none or junk. */
export const formatActivityDay = (day: string | null | undefined): string =>
	formatDay(day, "d MMM");

/** A measured count, or a dash when the window was never rolled up (unmeasured is not zero). */
export const formatActivityCount = (value: number | null | undefined): string =>
	value === null || value === undefined ? "—" : String(value);

/**
 * PREC-4336 part C: the "Activity" cell — recorded product events over the three complete-UTC-day
 * windows ending at the last rolled-up day. A dash when every window is zero or unmeasured.
 */
export const getActivitySummary = (
	stats: ActivityStats,
	lastRolledUpDate: string | null,
	t: TFunction
): string => {
	const total = (stats.day ?? 0) + (stats.week ?? 0) + (stats.month ?? 0);

	if (total === 0) {
		return "—";
	}

	const parts: string[] = [];

	if (stats.day !== null) {
		parts.push(
			t("supportDashboard.activity.summaryDay", {
				n: stats.day,
				date: formatActivityDay(lastRolledUpDate),
			})
		);
	}

	if (stats.week !== null) {
		parts.push(t("supportDashboard.activity.summaryWeek", { n: stats.week }));
	}

	if (stats.month !== null) {
		parts.push(t("supportDashboard.activity.summaryMonth", { n: stats.month }));
	}

	return parts.join(" · ");
};

/** The catalog label of a feature key, or the key itself when the catalog does not know it. */
export const getFeatureLabel = (key: string): string => FEATURE_LABELS.get(key) ?? key;

const formatDay = (day: string | null | undefined, pattern: string): string => {
	const parsed = day ? parseDay(day) : null;

	return parsed ? format(parsed, pattern) : "—";
};

/** A calendar day at noon local, so the displayed date is the UTC day in any timezone. */
const parseDay = (day: string): Date | null => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);

	if (!match) {
		return null;
	}

	const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);

	return Number.isNaN(date.getTime()) ? null : date;
};

export const SUBSCRIPTION_TYPE_LABELS: Record<SubscriptionType, string> = {
	FREE: "Free",
	FREE_TRIAL: "Free trial",
	PAID: "Paid",
	PAID_TRIAL: "Paid trial",
	FREE_EXPIRED: "Free – Expired",
};

export const BILLING_INTERVAL_LABELS: Record<BillingInterval, string> = {
	MONTHLY: "Monthly",
	EVERY_3_MONTHS: "Every 3 months",
	EVERY_6_MONTHS: "Every 6 months",
	YEARLY: "Yearly",
};

export const getSubscriptionBadgeClasses = (type: SubscriptionType): string => {
	switch (type) {
		case "PAID":
			return "bg-status-success-light text-status-success";
		case "PAID_TRIAL":
			return "bg-status-warning-light text-status-warning";
		case "FREE_EXPIRED":
			return "bg-status-error-light text-status-error";
		case "FREE":
		case "FREE_TRIAL":
		default:
			return "bg-inactive-item text-text-meta";
	}
};

/**
 * Badge tone for the "Errors 24h" column: red when any step needs attention,
 * amber when only recipient-side (expected) failures exist, neutral when none.
 */
export const getSendErrorBadgeClasses = (company: SupportCompany): string => {
	if (company.sendErrors.needsAttention > 0) {
		return "bg-status-error-light text-status-error";
	}

	if (company.sendErrors.total > 0) {
		return "bg-status-warning-light text-status-warning";
	}

	return "bg-inactive-item text-text-meta";
};

export const formatSubscriptionType = (type: SubscriptionType | null | undefined): string => {
	if (!type) {
		return "—";
	}

	return SUBSCRIPTION_TYPE_LABELS[type] ?? type;
};

export const formatBillingInterval = (interval: BillingInterval | null | undefined): string => {
	if (!interval) {
		return "—";
	}

	return BILLING_INTERVAL_LABELS[interval] ?? interval;
};

export const formatCredits = (value: number | null | undefined): string => {
	if (value === null || value === undefined) {
		return "—";
	}

	return String(value);
};

export interface TicketCounts {
	open: number;
	waitingOnUs: number;
}

/** `GET tickets/admin/counts` by company id, SQL aggregates coerced (they arrive as strings). */
export const ticketCountsByCompany = (
	rows: CompanyOpenTicketCount[] | undefined
): Map<number, TicketCounts> =>
	new Map(
		(rows ?? []).map((row) => [
			Number(row.companyId),
			{
				open: Number(row.openCount ?? 0) || 0,
				waitingOnUs: Number(row.waitingOnUsCount ?? 0) || 0,
			},
		])
	);
